
import { Component, Prop, Vue } from 'vue-property-decorator';
import PriceListRegionRepository from '@/repositories/PriceListRegionRepository';
import PriceTypeRepository from '@/repositories/PriceTypeRepository';
import DiscountInformationRow from '@/components/global/client/DiscountInformationRow.vue';
import { DiscountTableEntry } from '@/interfaces/components/clients/DiscountTableEntry';
import { UserRightsEnum } from '@/enums/global/UserRights';
import { EventBus } from '@/helpers/EventBusHelper';
import { SessionStorageService as ss } from '@/services/SessionStorageService';
import { fetchAndTransformMaterials } from '@/helpers/Discount/DiscountHelper';

@Component({
    name: 'DiscuntInformation',
    components: {
        DiscountInformationRow,
    },
})
export default class DiscuntInformation extends Vue {
    @Prop({ required: true }) private value!: any;
    @Prop({ required: false }) private deletedDiscountIds!: string[];
    @Prop({ required: false, default: false }) private isClientProtected!: boolean;

    private discounts: DiscountTableEntry[] = [...this.value.discounts];
    private loading: boolean = false;

    private get priceTypes(): any[] {
        return PriceTypeRepository.getAll();
    }

    private get priceListRegions(): any[] {
        return PriceListRegionRepository.getAll();
    }

    private get userRights() {
        return this.$store.getters['jwtData/userRights'];
    }

    public get canUserSeeDiscounts() {
        return this.userRights.includes(UserRightsEnum.VIEW_DISCOUNTS);
    }

    public get canUserEditDiscounts() {
        return this.userRights.includes(UserRightsEnum.EDIT_DISCOUNTS) && !this.isClientProtected;
    }

    public get canUserSeePriceTypeAndPricelist() {
        return this.userRights.includes(UserRightsEnum.VIEW_PRICETYPE_AND_PRICELIST);
    }

    public get canUserEditPriceTypeAndPricelist() {
        return this.userRights.includes(UserRightsEnum.EDIT_PRICETYPE_AND_PRICELIST);
    }

    private async beforeMount() {
        EventBus.$on('discountsUpdated', this.updateDiscounts);
        await this.prepareAndCacheDefaultMaterials();

        this.setDefaultValues();
    }

    private beforeDestroy() {
        EventBus.$off('discountsUpdated', this.updateDiscounts);
    }

    private setDefaultValues() {
        if (!this.value.priceType) {
            if (!this.priceTypes || this.priceTypes.length === 0) {
                return;
            }

            const defaultValue = this.priceTypes.find((pt) => pt.name === 'VK1');

            if (defaultValue) {
                this.value.priceType = defaultValue.id;
            } else {
                this.value.priceType = this.priceTypes[0].id;
            }
        }

        if (!this.value.priceListRegion) {
            if (!this.priceListRegions || this.priceListRegions.length === 0) {
                return;
            }

            const highestValue = this.priceListRegions.reduce((prev, current) => {
                const prevAbs = Math.abs(prev.name || 0);
                const currentAbs = Math.abs(current.name || 0);

                if (prevAbs > currentAbs) {
                    return prev;
                }

                return current;
            });

            if (highestValue) {
                this.value.priceListRegion = highestValue.id;
            } else {
                this.value.priceListRegion = this.priceListRegions[0].id;
            }
        }
    }

    private async prepareAndCacheDefaultMaterials() {
        if (!ss.has('materials')) {
            this.loading = true;
            const materials = await fetchAndTransformMaterials('');

            ss.stringifyAndSave('materials', materials);
            this.loading = false;
        }
    }

    private updateDiscounts() {
        this.discounts = [...this.value.discounts];
    }

    private emptyDiscount(): DiscountTableEntry {
        return {
            isEditing: true,
            isPercentage: true,
        };
    }

    private onAddDiscount() {
        this.discounts.unshift(this.emptyDiscount());
        this.updateIsEditingDiscounts();
    }

    private cancelChanges(i: number) {
        if (!this.value.discounts[i]) {
            this.discounts.splice(i, 1);
        }
    }

    private onDeleteDiscount(i: number) {
        if (this.deletedDiscountIds && this.discounts[i].id) {
            this.$emit('updateDeletedDiscountIds', this.discounts[i].id);
        }

        this.discounts.splice(i, 1);
    }

    private onUpdateDiscount(i: number) {
        this.value.discounts = [...this.discounts];
        this.$emit('input', this.value);
    }

    private updateIsEditingDiscounts() {
        this.$emit('updateIsEditingDiscounts', this.discounts.findIndex((d: DiscountTableEntry) => d.isEditing) >= 0);
    }
}
